import type { Uuid } from "@/domains/global/identifiers";
import { CurrentAssistantQueryObservable } from "@/store/assistant/current-assistant-query-observable";
import type { BaseSyncModel } from "@/store/sync/BaseSyncModel";
import { BaseSyncModelStore } from "@/store/sync/BaseSyncModelStore";
import type { SyncModelData, SyncModelKind, SyncUpdateValue } from "@/store/sync/types";
import type { AppSubStoreArgs } from "@/store/types";
import type { Table } from "dexie";
import { computed, makeObservable } from "mobx";
import { computedFn } from "mobx-utils";

type AssistantIndexedSyncUpdateValue<ModelData extends SyncModelData> =
  SyncUpdateValue<ModelData> & {
    assistant_id: Uuid;
  };

export abstract class BaseCurrentAssistantSyncModelStore<
  SyncModel extends BaseSyncModel<ModelData>,
  ModelData extends SyncModelData,
> extends BaseSyncModelStore<SyncModel, ModelData> {
  private readonly currentAssistantModelIdsQuery: CurrentAssistantQueryObservable<Uuid[]>;

  protected constructor({
    modelKind,
    ...injectedDeps
  }: { modelKind: SyncModelKind } & AppSubStoreArgs) {
    super({ modelKind, ...injectedDeps });

    this.currentAssistantModelIdsQuery = new CurrentAssistantQueryObservable({
      createEmptyData: () => [],
      query: async ({ assistantId }) => {
        const localTable = this.localTable as Table<AssistantIndexedSyncUpdateValue<ModelData>>;
        const modelIds = await localTable.where("assistant_id").equals(assistantId).primaryKeys();

        return modelIds as Uuid[];
      },
      store: this.store,
    });

    makeObservable<this, "currentAssistantModelIdsQuery">(this, {
      currentAssistantId: computed,
      currentAssistantModelIdsQuery: false,
      currentAssistantModels: computed,
      currentAssistantModelsLoaded: computed,
      currentAssistantModelsResolved: computed,
      currentAssistantModelDoesNotExist: false,
      getCurrentAssistantModelById: false,
    });
  }

  get currentAssistantId(): Uuid | undefined {
    return this.currentAssistantModelIdsQuery.state.assistantId;
  }

  get currentAssistantModels(): SyncModel[] {
    return this.getMany(this.currentAssistantModelIdsQuery.state.data);
  }

  get currentAssistantModelsLoaded(): boolean {
    return !this.currentAssistantModelIdsQuery.state.isLoading;
  }

  /**
   * Reports whether every current-assistant model ID has materialized into a model.
   * Successful empty queries are resolved; query failures remain unresolved.
   */
  get currentAssistantModelsResolved(): boolean {
    const queryState = this.currentAssistantModelIdsQuery.state;
    if (queryState.isLoading || queryState.isError) {
      return false;
    }

    return this.currentAssistantModels.length === queryState.data.length;
  }

  getCurrentAssistantModelById = computedFn((modelId: Uuid): SyncModel | undefined => {
    const currentAssistantModelIds = this.currentAssistantModelIdsQuery.state.data;

    if (!currentAssistantModelIds.includes(modelId)) {
      return undefined;
    }

    return this.get(modelId);
  });

  currentAssistantModelDoesNotExist = computedFn((modelId: Uuid): boolean => {
    if (!this.currentAssistantModelsLoaded) {
      return false;
    }

    return !this.currentAssistantModelIdsQuery.state.data.includes(modelId);
  });
}
